import "@tento-ui/components/data/json-viewer";

import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { DomUtils } from "@tento-ui/dom";
import { $$ } from "@tento-ui/fastdom";
import { Lisp } from "@tento-lisp/lisp";
import { ScrollShadows } from "@tento-ui/ui/scroll-shadows";
import { Typography } from "@tento-ui/ui/typography";
import { Button } from "@tento-ui/components/button/button";
import { Scrollbars } from "@tento-ui/components/scrollbars";
import type { CellEntry, Option } from "@tento-lona/sheets";
import { JsonViewer } from "@tento-ui/components/data/json-viewer";

const SCROLL_OVERFLOW_TOLERANCE_PX = 1;

/** How many dated items a data list shows before "show all". */
const INITIAL_SHOW_LIMIT = 8;

@component({
  name: "std-data-list-cell",
})
export class DataListCell extends LonaWebComponent {
  #showAll = false;
  #allDates: CellEntry[] = [];
  #allCustoms: CellEntry.Timepoint<string>[] = [];

  #handleScroll = () => {
    this.#updateScrollShadows();
  };

  connectedCallback() {
    this.$("content").addEventListener("scroll", this.#handleScroll);
    // Check initial scroll state
    this.#updateScrollShadows();
  }

  disconnectedCallback() {
    this.$("content").removeEventListener("scroll", this.#handleScroll);
  }

  #updateScrollShadows(): void {
    const $content = this.$("content");
    ScrollShadows.updateScrollShadows(this, $content);
  }

  bind(dates: CellEntry[], customs: CellEntry.Timepoint<string>[]) {
    this.#allDates = dates;
    this.#allCustoms = customs;
    this.#renderItems();
  }

  /** Bind for the expanded popup: render every entry (no "show more" cap). */
  bindExpanded(dates: CellEntry[], customs: CellEntry.Timepoint<string>[]): void {
    this.#showAll = true;
    this.#allDates = dates;
    this.#allCustoms = customs;
    this.#renderItems();
  }

  /** True when any rendered entry is clipped on the x-axis — the signal that
   *  the full-width expand popup would show more than the cell can. */
  hasHorizontalOverflow(): boolean {
    for (const $child of Array.from(this.$("content").children)) {
      if ($child instanceof DatedJson && $child.hasHorizontalOverflow()) {
        return true;
      }
    }
    return false;
  }

  #renderItems() {
    DomUtils.clearChildren(this.$("content"));

    const totalItems =
      this.#allDates.filter((d) => d.value != null).length +
      this.#allCustoms.length;
    const shouldShowButton = totalItems > INITIAL_SHOW_LIMIT;

    let itemsAdded = 0;
    const itemLimit = this.#showAll
      ? Number.MAX_SAFE_INTEGER
      : INITIAL_SHOW_LIMIT;

    for (const date of this.#allDates) {
      if (date.value == null) continue;
      if (itemsAdded >= itemLimit) break;

      const $json = DatedJson.makeWith(
        date.date.toStringReadable(),
        date.value,
      );
      this.$("content").appendChild($json);
      itemsAdded++;
    }

    for (const custom of this.#allCustoms) {
      if (itemsAdded >= itemLimit) break;
      if (custom.value == null) continue;

      const start = custom.dtr.start;
      const end = custom.dtr.end;
      if (start.mse === end.mse) {
        const $json = DatedJson.makeWith(start.toString(), custom.value);
        this.$("content").appendChild($json);
      } else {
        const $json = DatedJson.makeWith(
          `${start.toString()}..${end.toString()}`,
          custom.value,
        );
        this.$("content").appendChild($json);
      }
      itemsAdded++;
    }

    if (shouldShowButton && !this.#showAll) {
      const remainingItems = totalItems - itemsAdded;
      const $button = Button.makeWithLight(`Show ${remainingItems} More`, {
        type: "flat",
        onClick: () => {
          this.#showAll = true;
          this.#renderItems();
        },
      });
      const $buttonContainer = document.createElement("div");
      $buttonContainer.style.padding = "12px";
      $buttonContainer.style.textAlign = "center";
      $buttonContainer.appendChild($button);
      this.$("content").appendChild($buttonContainer);
    }

    // Update shadows after content changes
    $$.mutate(() => this.#updateScrollShadows());
  }

  static $styles: Style.Compat[] = [
    Scrollbars.$hideScrollbars,
    ScrollShadows.$styles,
    Button.$style,
    css`
      :host {
        display: block;
        height: calc(100% - 2px);
        margin-block: 1px;
        margin-inline: 2px;
        position: relative;
        width: calc(100% - 6px);
      }

      #root {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
        border-radius: 8px;
      }

      #content {
        overflow-y: scroll;
        width: 100%;
        height: 100%;
        padding: 6px 12px;
      }

      .scroll-shadow-top,
      .scroll-shadow-bottom {
        left: 16px;
        right: 16px;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id=root>
      <div class="scroll-shadow-top"></div>
      <div class="scroll-shadow-bottom"></div>
      <std-col id=content></std-col>
    </std-col>
  `;
}

@component({
  name: "std-dated-json",
})
export class DatedJson extends LonaWebComponent {
  #$jsonViewer = this.hydrate<JsonViewer>("json-viewer");
  static makeWith(date: string, data: Lisp.Atom): DatedJson {
    const $e = DatedJson.make();
    $e.bind(date, data);
    return $e;
  }

  bind(date: string, data: Lisp.Atom) {
    this.$("date").textContent = date;
    this.#$jsonViewer.bind(data);
  }

  /** Whether this entry's JSON content is wider than its (x-clipped) box. */
  hasHorizontalOverflow(): boolean {
    const $content = this.$("content");
    return (
      $content.scrollWidth - $content.clientWidth > SCROLL_OVERFLOW_TOLERANCE_PX
    );
  }

  static $styles: Style.Compat[] = [
    Typography.$maxLines,
    css`
      :host {
        padding-block: 8px;
      }

      #date {
        color: var(--text-ter);
        margin-bottom: 6px;
        padding-inline: 8px;
      }

      #content {
        background: var(--bg-elevated);
        border-radius: 8px;
        box-shadow: var(--box-shadow-very-light);
        padding: 12px;
        margin-inline: -4px;
        overflow-x: hidden;
      }

      std-json-viewer {
        display: block;
      }
    `,
  ];
  static $html: Option<HTMLTemplateElement> = html`
    <std-col id=root>
      <p id=date class="small max-lines-2"></p>
      <div id=content>
        <std-json-viewer id="json-viewer"></std-json-viewer>
      </div>
    </std-col>
  `;
}
